/**
 * SnapshotComparisonTab - Shows comparison between data at issue creation vs current values.
 */

import { format } from 'date-fns'
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Key,
  RefreshCw,
  XCircle,
  AlertCircle,
  FileWarning,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useSnapshotComparison } from '@/hooks/useAnnotations'
import type { Annotation, SnapshotFieldComparison } from '@/types/annotation'

interface SnapshotComparisonTabProps {
  issue: Annotation
  studyId: string
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '(empty)'
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

export function SnapshotComparisonTab({ issue, studyId }: SnapshotComparisonTabProps) {
  const { data: comparison, isLoading, error, refetch } = useSnapshotComparison(studyId, issue.id)

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load comparison data. {error instanceof Error ? error.message : 'Unknown error'}
        </AlertDescription>
      </Alert>
    )
  }

  if (!comparison) {
    return (
      <Alert>
        <FileWarning className="h-4 w-4" />
        <AlertTitle>No Snapshot Data</AlertTitle>
        <AlertDescription>
          No data snapshot was captured when this issue was created.
          This may be because the issue was created before snapshot capture was enabled.
        </AlertDescription>
      </Alert>
    )
  }

  // Record status alerts
  const statusAlerts = {
    deleted: {
      variant: 'destructive' as const,
      icon: XCircle,
      title: 'Record Deleted',
      description: 'This record no longer exists in the current data. Showing values captured at issue creation.',
      className: '',
    },
    not_found: {
      variant: 'destructive' as const,
      icon: XCircle,
      title: 'Record Not Found',
      description: 'Could not locate this record in the current data.',
      className: '',
    },
    multiple_matches: {
      variant: 'default' as const,
      icon: AlertTriangle,
      title: 'Multiple Matches',
      description: 'Multiple records match the original key. This may indicate key column changes. Showing first match.',
      className: 'border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-400',
    },
    found: null,
  }

  const statusAlert = statusAlerts[comparison.recordStatus]

  // Count changes
  const changedFields = comparison.fields.filter((f: SnapshotFieldComparison) => f.changed)
  const hasChanges = changedFields.length > 0

  return (
    <div className="space-y-4 p-4">
      {/* Header with timestamps */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            Comparing data from {format(new Date(comparison.createdAt), 'PPpp')} (issue creation)
            {' vs '}
            {format(new Date(comparison.currentQueryTime), 'PPpp')} (current)
          </span>
        </div>
        <button
          onClick={() => refetch()}
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      {/* Key columns change warning */}
      {comparison.keyColumnsChanged && (
        <Alert variant="default" className="border-yellow-500 bg-yellow-50 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-400">
          <Key className="h-4 w-4" />
          <AlertTitle>Key Columns Changed</AlertTitle>
          <AlertDescription>
            The key columns configuration has changed since this issue was created.
            <br />
            <span className="text-xs">
              Original: {comparison.keyColumnsAtCreation.join(', ') || 'None'}
              {' → '}
              Current: {comparison.currentKeyColumns.join(', ') || 'None'}
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* Record status alert */}
      {statusAlert && (
        <Alert variant={statusAlert.variant} className={statusAlert.className}>
          <statusAlert.icon className="h-4 w-4" />
          <AlertTitle>{statusAlert.title}</AlertTitle>
          <AlertDescription>{statusAlert.description}</AlertDescription>
        </Alert>
      )}

      {/* Changes summary */}
      <div className="flex items-center gap-2">
        {hasChanges ? (
          <>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <span className="text-sm font-medium">
              {changedFields.length} field{changedFields.length !== 1 ? 's' : ''} changed since issue creation
            </span>
          </>
        ) : comparison.recordStatus === 'found' ? (
          <>
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium">No changes detected</span>
          </>
        ) : null}
      </div>

      {/* Comparison table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/3">Field</TableHead>
              <TableHead className="w-1/3">
                Current Value
                <span className="block text-xs font-normal text-muted-foreground">
                  {format(new Date(comparison.currentQueryTime), 'PP')}
                </span>
              </TableHead>
              <TableHead className="w-1/3">
                Issue Creation Value
                <span className="block text-xs font-normal text-muted-foreground">
                  {format(new Date(comparison.createdAt), 'PP')}
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comparison.fields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  No field data available
                </TableCell>
              </TableRow>
            ) : (
              comparison.fields.map((field: SnapshotFieldComparison) => {
                const isKeyColumn = comparison.keyColumnsAtCreation.includes(field.field)
                return (
                  <TableRow
                    key={field.field}
                    className={field.changed ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}
                  >
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-1">
                        {field.field}
                        {isKeyColumn && (
                          <Badge variant="outline" className="text-xs ml-1">
                            <Key className="h-2 w-2 mr-1" />
                            Key
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {comparison.recordStatus === 'found' ? (
                        <div className="flex items-center gap-2">
                          <span className={field.changed ? 'text-yellow-700 dark:text-yellow-400 font-medium' : ''}>
                            {formatValue(field.currentValue)}
                          </span>
                          {field.changed && (
                            <AlertTriangle className="h-3 w-3 text-yellow-500" />
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic">N/A</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatValue(field.creationValue)}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Record key info */}
      <div className="text-xs text-muted-foreground">
        <span className="font-medium">Record Key:</span>{' '}
        {Object.entries(comparison.recordKey || {}).map(([k, v]) => (
          <span key={k} className="mr-2">
            {k}={String(v)}
          </span>
        ))}
      </div>
    </div>
  )
}

export default SnapshotComparisonTab
